import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { BookCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Alert, Button, Card, PageContainer, PageHeader } from "@/components/ui";

interface SolicitudPorCertificar {
  id: string;
  folio: string;
  fechaCreacion: string;
  servicioCodigo: string;
  servicioNombre: string | null;
  titularRazonSocial: string;
  titularRfc: string;
  productoNombre: string;
  productoModelo: string;
  marca: string;
}

/**
 * Bandeja "Por certificar" — solicitudes en estado ACEPTADA del flujo v2 que
 * emiten certificado (CERTIFICADO_UNICO o REEXPEDICION_PC) y aún no lo tienen.
 * Espejo de la bandeja del portlet legacy CertificaConformidadUnica.
 */
export function SolicitudesPorCertificarPage() {
  const navigate = useNavigate();
  const q = useQuery<SolicitudPorCertificar[]>({
    queryKey: ["solicitudes-por-certificar"],
    queryFn: async () =>
      (await api.get<SolicitudPorCertificar[]>("/api/sector/telecom/solicitudes-por-certificar")).data,
  });

  return (
    <PageContainer width="wide">
      <PageHeader
        backTo="/telecom"
        backLabel="Volver a Telecom"
        icon={<BookCheck className="h-5 w-5" />}
        title="Por certificar"
        subtitle="Solicitudes Telecom aceptadas que aún no tienen certificado emitido. Selecciona una para elaborar el certificado (Único o Reexpedido)."
      />

      <Card padding="none">
        {q.isLoading && <div className="p-6 text-sm text-fg-subtle">Cargando solicitudes…</div>}
        {q.error && (
          <div className="p-4"><Alert variant="warning">No se pudieron cargar las solicitudes.</Alert></div>
        )}
        {q.data && (
          <>
            <div className="border-b border-edge/60 px-4 py-2 text-xs uppercase tracking-wide text-fg-subtle">
              {q.data.length} solicitud{q.data.length === 1 ? "" : "es"} por certificar
            </div>
            {q.data.length === 0 ? (
              <div className="p-6 text-sm text-fg-subtle">
                No hay solicitudes aceptadas pendientes de certificación.
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-2">
                  <tr>
                    {["Folio", "Titular", "Servicio", "Producto", "Marca", ""].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge bg-surface">
                  {q.data.map((r) => (
                    <tr key={r.id} className="transition hover:bg-primary-tint">
                      <td className="px-4 py-3 align-top">
                        <span className="font-mono text-xs">{r.folio}</span>
                        <div className="text-[10px] text-fg-subtle">{formatDate(r.fechaCreacion)}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-fg">{r.titularRazonSocial}</div>
                        <div className="text-[10px] font-mono text-fg-subtle">{r.titularRfc}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-xs">{r.servicioNombre ?? r.servicioCodigo}</td>
                      <td className="px-4 py-3 align-top text-xs">
                        {r.productoNombre} {r.productoModelo}
                      </td>
                      <td className="px-4 py-3 align-top text-xs">{r.marca}</td>
                      <td className="px-4 py-3 align-top text-right">
                        <Button
                          size="sm"
                          icon={<BookCheck className="h-3 w-3" />}
                          onClick={() => navigate(`/telecom/certificados/nuevo?solicitudId=${r.id}`)}
                        >
                          Certificar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </>
        )}
      </Card>
    </PageContainer>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return s;
  }
}
